import { getToken } from 'firebase/app-check';
import { appCheck, auth } from '@/lib/firebase';
import {
  documentFileTypeFromName,
  isDocumentAttachment,
  MAX_DOCUMENT_TOTAL_BYTES,
  validateDocumentBytes,
  type DocumentAttachment,
} from '@/lib/research-assistant/document-attachments';

async function attachmentError(response: Response, fallback: string): Promise<Error> {
  const payload = (await response.json().catch(() => null)) as {
    error?: unknown;
    message?: unknown;
  } | null;
  const message =
    typeof payload?.error === 'string'
      ? payload.error
      : typeof payload?.message === 'string'
        ? payload.message
        : fallback;
  return new Error(message);
}

function attachmentRoute(
  projectId: string | null | undefined,
  workspaceId?: string | null,
): string {
  const normalized = projectId?.trim() || '';
  if (!normalized && workspaceId?.trim())
    return `/api/research-assistant/attachments?workspaceId=${encodeURIComponent(workspaceId.trim())}`;
  if (!normalized) throw new Error('Select a project before uploading a document.');
  return `/api/research-assistant/attachments?projectId=${encodeURIComponent(normalized)}`;
}

async function appCheckToken(): Promise<string> {
  if (!appCheck) throw new Error('App verification is unavailable. Refresh and try again.');
  return (await getToken(appCheck, false)).token;
}

export async function uploadDocumentAttachment(
  file: File,
  projectId: string | null,
  workspaceId?: string | null,
): Promise<DocumentAttachment> {
  const user = auth.currentUser;
  if (!user) throw new Error('Sign in before uploading a document.');

  const fileType = documentFileTypeFromName(file.name);
  if (!fileType) {
    throw new Error('Unsupported document type.');
  }
  if (file.size <= 0) throw new Error('The document is empty.');
  if (file.size > MAX_DOCUMENT_TOTAL_BYTES) {
    throw new Error('Documents must be 10 MB or smaller.');
  }

  const bytes = new Uint8Array(await file.arrayBuffer());
  const validationError = validateDocumentBytes(bytes, fileType);
  if (validationError) throw new Error(validationError);

  const [token, attestation] = await Promise.all([user.getIdToken(), appCheckToken()]);
  const response = await fetch(attachmentRoute(projectId, workspaceId), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'X-Firebase-AppCheck': attestation,
      'Content-Type': fileType.mimeType,
      'X-Attune-Document-Name': encodeURIComponent(file.name),
    },
    body: bytes,
  });
  if (!response.ok) {
    throw await attachmentError(response, 'Failed to upload document.');
  }
  const payload = (await response.json().catch(() => null)) as { attachment?: unknown } | null;
  const attachment = payload?.attachment;
  if (!isDocumentAttachment(attachment)) {
    throw new Error('The upload returned invalid document metadata.');
  }
  return attachment;
}

export async function deleteUploadedDocument(
  attachment: DocumentAttachment,
  projectId: string | null | undefined = attachment.projectId,
): Promise<void> {
  const user = auth.currentUser;
  if (!user || !isDocumentAttachment(attachment)) return;
  const [token, attestation] = await Promise.all([user.getIdToken(), appCheckToken()]);
  const response = await fetch(attachmentRoute(projectId, attachment.workspaceId), {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${token}`,
      'X-Firebase-AppCheck': attestation,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ attachment }),
  });
  if (!response.ok) {
    throw await attachmentError(response, 'Failed to remove document.');
  }
}
